import React, { useMemo } from 'react'
import { Card, Divider, Label } from 'semantic-ui-react'
import { CommonUtility, TemplateResponseType, isFormulaQuestion } from 'utility'
import styled from 'styled-components'
import { Check } from 'phosphor-react'
import { useTabletAndBelowMediaQuery } from 'layout'
import { AppEditButton, AppRemoveButtonPopup, AppTable, FlexBox, FlexRow } from 'components'

const QuestionsContainer = styled.div`
    .card {
        width: 100% !important;
    }
        .description {
            padding: 0 !important;
        }
`

const NestedBadgeIcon = styled.span`
    display: inline-flex;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #fff;
    color: #14532d;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`

const NestedBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-left: 8px;
    padding: 3px 10px 3px 5px;
    border-radius: 6px;
    background: #14532d;
    color: #dcfce7;
    font-size: 0.75rem;
    font-weight: 600;
    vertical-align: middle;
    line-height: 1.2;
`

const answerTypeLabel = item => {
    if (isFormulaQuestion(item)) return 'Formula'
    if (item.QuestionType === TemplateResponseType.Multiple) return 'Multiple'
    return 'Single'
}

const QuestionCard = ({
    item,
    i,
    edit,
    remove,
    onDragStart,
    onDragOver,
    onDragEnd,
    onDrop,
    draggingIndex,
    showNumbers = false,
    startNumber = 1,
}) => {
    return (
        <Card
            key={item.Id}
            draggable
            className={`draggable ${draggingIndex === i ? 'dragging' : ''}`}
            onDragStart={e => onDragStart(e, i)}
            onDragOver={e => onDragOver(e)}
            onDragEnd={onDragEnd}
            onTouchStart={e => onDragStart(e, i)}
            onTouchMove={onDragOver}
            onTouchEnd={onDragEnd}
            onTouchCancel={onDragEnd}
            onDrop={e => {
                onDrop(e, i)
            }}
        >
            <Card.Content>
                <Card.Header>
                    <FlexRow>
                        <span className="col-8">
                            {showNumbers && (
                                <strong className="me-2">
                                    {(item.__rowIndex || 0) + startNumber}.
                                </strong>
                            )}
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: CommonUtility.strippedHtml(
                                        item.Question,
                                    ),
                                }}
                            />
                            {item.IsNestedQuestion === true && (
                                <NestedBadge>
                                    <NestedBadgeIcon>
                                        <Check size={10} weight="bold" />
                                    </NestedBadgeIcon>
                                    Nested
                                </NestedBadge>
                            )}
                        </span>
                        <span className="col-4 justify-content-end d-flex">
                            <AppEditButton
                                icon="edit"
                                onClick={() => edit(item)}
                            />
                            <AppRemoveButtonPopup
                                icon="trash"
                                onClick={() => remove(item)}
                            />
                        </span>
                    </FlexRow>
                </Card.Header>
                <Divider className="mt-2" />
                <Card.Description className="pb-0 p-0 d-flex">
                    <span className="col-6">Answer Type:</span>
                    <div className="col-6 d-flex justify-content-end">
                        {answerTypeLabel(item)}
                    </div>
                </Card.Description>
                <Card.Description className="pb-0 p-0 d-flex">
                    <span className="col-6">Required:</span>
                    <div className="col-6 d-flex justify-content-end">
                        {item.IsRequired ? 'Yes' : 'No'}
                    </div>
                </Card.Description>
            </Card.Content>
        </Card>
    )
}
export const QuestionList = ({
    questions,
    edit,
    remove,
    onDragCompleted,
    showNumbers = false,
    startNumber = 1,
}) => {
    const isMobile = useTabletAndBelowMediaQuery()

    const numberedData = useMemo(
        () => questions.map((q, i) => ({ ...q, __rowIndex: i })),
        [questions],
    )

    const columns = useMemo(
        () => [
            {
                title: 'Question',
                render: item => {
                    const text = CommonUtility.strippedHtml(item.Question)
                    return (
                        <>
                            {showNumbers && (
                                <strong className="me-2">
                                    {(item.__rowIndex || 0) + startNumber}.
                                </strong>
                            )}
                            <span>{text}</span>
                            {item.IsNestedQuestion === true && (
                                <NestedBadge>
                                    <NestedBadgeIcon>
                                        <Check size={10} weight="bold" />
                                    </NestedBadgeIcon>
                                    Nested
                                </NestedBadge>
                            )}
                            {item.IsRequired && (
                                <Label className="ms-2">Required</Label>
                            )}
                        </>
                    )
                },
            },
            {
                title: 'Response',
                render: item => (
                    <>
                        {answerTypeLabel(item)}
                    </>
                ),
            },
            {
                title: 'Actions',
                render: item => (
                    <FlexBox className="justify-content-center">
                        <AppEditButton onClick={() => edit(item)} />
                        <AppRemoveButtonPopup
                            onConfirm={() => remove(item)}
                        />
                    </FlexBox>
                ),
            },
        ],
        [questions, showNumbers, startNumber, isMobile],
    )
    return (
        <QuestionsContainer className="mt-3">
            <AppTable
                data={numberedData}
                columns={columns}
                draggable
                onDragCompleted={onDragCompleted}
                mobileCard={
                    <QuestionCard
                        remove={remove}
                        edit={edit}
                        showNumbers={showNumbers}
                        startNumber={startNumber}
                    />
                }
                hidePagination
            />
        </QuestionsContainer>
    )
}
